import { Browser } from "@capacitor/browser";
import { completePhoneVerification } from "@/app/actions/verification";
import { logNativeFlow } from "@/lib/native-flow-diagnostics";

/**
 * Capacitor 본인인증 브릿지 deep link 핸들러.
 *
 * 포맷: locarora://identity-complete?identityVerificationId=...
 *                                  &code=...&message=...&pgCode=...&pgMessage=...
 *
 * 본인인증 시작 시 useKoreanPhoneVerification 이 외부 Safari.app 으로
 * /<locale>/verify-phone-external 페이지를 열어 거기서 V2 PortOne identity
 * SDK 를 실행한다 (SFSafariViewController 안에서는 V2 SDK 내부 IPC 가
 * hang 되므로 OS 레벨 [[UIApplication openURL:]] 로 별도 process 의
 * Safari 에 핸드오프). 인증 완료 시 PortOne SDK 가
 * /<locale>/identity-bridge-callback 으로 redirect, 그 페이지가 즉시 위
 * deep link 로 redirect → iOS 가 Capacitor 앱 활성화 → appUrlOpen 리스너
 * (`CapacitorBootstrap`) 가 이 함수를 호출.
 *
 * 처리:
 *   1. Browser.close — 외부 Safari 에서는 no-op (Browser plugin 으로 연
 *      SFSafariViewController 가 없음). 결제 flow 잔재 등 다른 경로로
 *      Browser 가 열려 있는 방어용으로 호출은 유지.
 *   2. 결과 코드 / 메시지에 따라 분기:
 *      - 사용자 취소 → 현재 페이지 유지, 토스트 없이 silent
 *      - 에러 → 에러 토스트
 *      - 성공 → completePhoneVerification(V2 server action) 호출 후 홈으로 이동
 *
 * 반환: 이 deep link 가 우리 책임이면 true.
 */

/**
 * NOTE: Deep link dedup 은 CapacitorBootstrap dispatch level 에서 중앙
 * 처리한다 (`@/lib/auth/deep-link-dedup`). 같은 패턴이 oauth / payment /
 * identity 핸들러 모두에 필요하므로 한 곳에서 막아주는 게 일관적임.
 */

export async function processIdentityDeepLink(
  urlStr: string,
): Promise<boolean> {
  let url: URL;
  try {
    url = new URL(urlStr);
  } catch {
    return false;
  }

  if (url.protocol !== "locarora:" || url.host !== "identity-complete") {
    return false;
  }

  const params = url.searchParams;
  const identityVerificationId = params.get("identityVerificationId") ?? "";
  const code = params.get("code");
  const message = params.get("message");
  const pgCode = params.get("pgCode");
  const pgMessage = params.get("pgMessage");
  // 사용자가 verify-phone 단계에서 입력한 phone — bridge page 가 callback URL
  // 에 실어 보낸다. PortOne API 응답의 verifiedCustomer.phoneNumber 가 빈 경우
  // server action fallback 으로 쓰임.
  const userInputPhone = params.get("phone") || undefined;

  logNativeFlow("identity-deep-link", "received", {
    identityVerificationId,
    code,
    message,
    pgCode,
    pgMessage,
  });

  // SFSafariViewController 닫기
  try {
    await Browser.close();
  } catch {
    // 이미 닫혔거나 Android에서 실패해도 UX 영향 없음
  }

  const pathSegments = window.location.pathname.split("/").filter(Boolean);
  const locale = pathSegments[0] || "ko";

  if (code) {
    if (code === "USER_CANCEL") {
      // 사용자 취소 — 현재 페이지 유지, silent
      logNativeFlow("identity-deep-link", "user-cancelled", {
        identityVerificationId,
      });
      return true;
    }
    logNativeFlow(
      "identity-deep-link",
      "error",
      { identityVerificationId, code, message },
      "warn",
    );
    const errorParam = encodeURIComponent(message || code);
    window.location.href = `/${locale}/verify-phone?error=${errorParam}`;
    return true;
  }

  if (!identityVerificationId) {
    logNativeFlow(
      "identity-deep-link",
      "missing-id",
      {},
      "warn",
    );
    window.location.href = `/${locale}/verify-phone`;
    return true;
  }

  // 서버측 검증 + customer_profiles 업데이트
  try {
    logNativeFlow("identity-deep-link", "complete-verification.start", {
      identityVerificationId,
    });
    const result = await completePhoneVerification(
      identityVerificationId,
      "danal",
      "KR",
      userInputPhone,
    );
    logNativeFlow(
      "identity-deep-link",
      "complete-verification.done",
      {
        identityVerificationId,
        success: result.success,
        errorMessage: result.error,
      },
      result.success ? "info" : "warn",
    );

    if (!result.success) {
      const errorParam = encodeURIComponent(result.error || "UNKNOWN_ERROR");
      window.location.href = `/${locale}/verify-phone?error=${errorParam}`;
      return true;
    }

    // 성공 — 홈으로 이동 (기존 verify-phone-client.tsx 의 handleSuccess 동작과 동일)
    window.location.href = `/${locale}`;
    return true;
  } catch (err) {
    logNativeFlow(
      "identity-deep-link",
      "complete-verification.threw",
      {
        identityVerificationId,
        errorMessage: err instanceof Error ? err.message : String(err),
      },
      "error",
    );
    console.error("[identity-deep-link] complete failed", err);
    window.location.href = `/${locale}/verify-phone?error=processing_failed`;
    return true;
  }
}
